import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message, notification } from 'antd';
import { uploadCollabFile } from '../../services/teamCollabApi';

export const OFFICE_ATTACHMENT_LIMIT = 5;
export const OFFICE_ATTACHMENT_ACCEPT = '.txt,.md,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.png,.jpg,.jpeg,.webp,.gif,.bmp';

const SUPPORTED_EXTENSIONS = new Set([
  'txt', 'md', 'doc', 'docx', 'xls', 'xlsx', 'pdf',
  'ppt', 'pptx', 'png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp',
]);
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp']);
const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

export interface OfficeAttachment {
  id: string;
  name: string;
  content: string;
  uploading: boolean;
  error?: string;
  charCount?: number;
  tokenEstimate?: number;
  progressMsg?: string;
  previewUrl?: string;
  sourceFile: File;
  fingerprint: string;
}

export interface OfficeAttachmentPayload {
  fileContent?: string;
  fileName?: string;
}

const extensionOf = (name: string) => name.split('.').pop()?.toLowerCase() || '';
const fingerprintOf = (file: File) => `${file.name}:${file.size}:${file.lastModified}`;

/**
 * AI 指挥办公室附件状态。
 * 上传、解析、PDF/图片 OCR 完全复用智能工坊的 SSE 接口，避免两套处理结果不一致。
 */
export function useOfficeAttachments() {
  const [attachments, setAttachments] = useState<OfficeAttachment[]>([]);
  const attachmentsRef = useRef<OfficeAttachment[]>([]);
  const previewUrlsRef = useRef(new Set<string>());

  const commit = useCallback((updater: (current: OfficeAttachment[]) => OfficeAttachment[]) => {
    // ref 先同步，确保连续拖入/粘贴或密集 SSE 进度事件不会读到旧附件列表。
    const next = updater(attachmentsRef.current);
    attachmentsRef.current = next;
    setAttachments(next);
  }, []);

  const releasePreview = useCallback((url?: string) => {
    if (!url || !previewUrlsRef.current.has(url)) return;
    URL.revokeObjectURL(url);
    previewUrlsRef.current.delete(url);
  }, []);

  useEffect(() => () => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current.clear();
  }, []);

  const uploadOne = useCallback(async (id: string, file: File) => {
    try {
      const result = await uploadCollabFile(file, (progressMsg) => {
        commit((current) => current.map((item) => (
          item.id === id ? { ...item, progressMsg: progressMsg || item.progressMsg } : item
        )));
      });
      if (result.success && result.content) {
        // 用户可能在耗时 OCR 期间已移除该附件；此时静默丢弃结果。
        if (!attachmentsRef.current.some((item) => item.id === id)) return;
        commit((current) => current.map((item) => item.id === id ? {
          ...item,
          name: result.fileName || file.name,
          content: result.content || '',
          uploading: false,
          error: undefined,
          progressMsg: undefined,
          charCount: result.charCount || result.content?.length || 0,
          tokenEstimate: result.tokenEstimate,
        } : item));
        message.success(`${file.name} 已解析，可作为任务参考`);
        return;
      }
      const error = result.error || '文件解析失败';
      commit((current) => current.map((item) => item.id === id ? {
        ...item, uploading: false, progressMsg: undefined, error,
      } : item));
      notification.error({ message: `${file.name} 解析失败`, description: error, duration: 5, placement: 'topRight' });
    } catch (e: any) {
      const error = Array.isArray(e?.response?.data?.message)
        ? e.response.data.message.join('；')
        : e?.response?.data?.message || e?.response?.data?.error || e?.message || '网络异常，请稍后重试';
      commit((current) => current.map((item) => item.id === id ? {
        ...item, uploading: false, progressMsg: undefined, error,
      } : item));
      notification.error({ message: `${file.name} 上传失败`, description: error, duration: 5, placement: 'topRight' });
    }
  }, [commit]);

  const addFiles = useCallback(async (fileList: FileList | File[]) => {
    const incoming = Array.from(fileList);
    if (!incoming.length) return;

    const current = attachmentsRef.current;
    const slots = OFFICE_ATTACHMENT_LIMIT - current.length;
    if (slots <= 0) {
      notification.warning({ message: '附件已满', description: `每次最多引用 ${OFFICE_ATTACHMENT_LIMIT} 个文件`, placement: 'topRight' });
      return;
    }

    const known = new Set(current.map((item) => item.fingerprint));
    const accepted: File[] = [];
    let unsupported = 0;
    let oversized = 0;
    let duplicated = 0;
    for (const file of incoming) {
      const ext = extensionOf(file.name);
      if (!SUPPORTED_EXTENSIONS.has(ext)) { unsupported += 1; continue; }
      if (file.size > MAX_FILE_SIZE) { oversized += 1; continue; }
      const fingerprint = fingerprintOf(file);
      if (known.has(fingerprint)) { duplicated += 1; continue; }
      known.add(fingerprint);
      if (accepted.length < slots) accepted.push(file);
    }

    if (unsupported) notification.warning({ message: '有文件格式不支持', description: '支持 Word、Excel、PowerPoint、PDF、文本和常用图片格式', placement: 'topRight', duration: 6 });
    if (oversized) notification.warning({ message: '文件超过 100MB', description: `当前支持单个不超过 ${MAX_FILE_SIZE_MB}MB 的附件`, placement: 'topRight', duration: 6 });
    if (duplicated) message.info('已忽略重复附件');
    if (incoming.length - unsupported - oversized - duplicated > slots) {
      notification.warning({ message: '已达到附件上限', description: `本次仅加入前 ${slots} 个有效文件`, placement: 'topRight' });
    }
    if (!accepted.length) return;

    const pending = accepted.map((file, index): OfficeAttachment => {
      const ext = extensionOf(file.name);
      const previewUrl = IMAGE_EXTENSIONS.has(ext) ? URL.createObjectURL(file) : undefined;
      if (previewUrl) previewUrlsRef.current.add(previewUrl);
      return {
        id: `${Date.now()}_${index}_${Math.random().toString(36).slice(2, 8)}`,
        name: file.name,
        content: '',
        uploading: true,
        progressMsg: ext === 'pdf' ? '准备 PDF OCR…' : ext === 'ppt' || ext === 'pptx' ? '准备解析 PowerPoint…' : IMAGE_EXTENSIONS.has(ext) ? '准备图片识别…' : '正在解析…',
        previewUrl,
        sourceFile: file,
        fingerprint: fingerprintOf(file),
      };
    });
    commit((items) => [...items, ...pending]);

    // 与智能工坊一致按顺序处理，避免多个大 PDF 同时 OCR 挤占服务资源。
    for (const item of pending) await uploadOne(item.id, item.sourceFile);
  }, [commit, uploadOne]);

  const removeAttachment = useCallback((id: string) => {
    const target = attachmentsRef.current.find((item) => item.id === id);
    releasePreview(target?.previewUrl);
    commit((current) => current.filter((item) => item.id !== id));
  }, [commit, releasePreview]);

  const retryAttachment = useCallback((id: string) => {
    const target = attachmentsRef.current.find((item) => item.id === id);
    if (!target || target.uploading) return;
    commit((current) => current.map((item) => item.id === id ? {
      ...item, uploading: true, error: undefined, progressMsg: '正在重新解析…', content: '',
    } : item));
    void uploadOne(id, target.sourceFile);
  }, [commit, uploadOne]);

  const clearAttachments = useCallback(() => {
    attachmentsRef.current.forEach((item) => releasePreview(item.previewUrl));
    commit(() => []);
  }, [commit, releasePreview]);

  const usableAttachments = useMemo(
    () => attachments.filter((item) => !item.uploading && !item.error && !!item.content),
    [attachments],
  );
  const uploading = attachments.some((item) => item.uploading);
  const buildPayload = useCallback((): OfficeAttachmentPayload => {
    const usable = attachmentsRef.current.filter((item) => !item.uploading && !item.error && !!item.content);
    return usable.length ? {
      fileContent: usable.map((item, index) => `【附件${index + 1}：${item.name}】\n${item.content}`).join('\n\n---\n\n'),
      fileName: usable.map((item) => item.name).join('、'),
    } : {};
  }, []);

  return {
    attachments,
    usableAttachments,
    uploading,
    buildPayload,
    addFiles,
    removeAttachment,
    retryAttachment,
    clearAttachments,
  };
}
